import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  RegistryCompanySearchParams,
  RegistryCompanySearchResult,
  RegistryProduct,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';

/**
 * The documents Armadillo offers for one country, with price and turnaround.
 *
 * `enabled` gates the fetch so a page never reads the catalogue until the
 * analyst is choosing documents; a country change re-reads it. A failed
 * read is an `error` for the caller to show — there is no order to place
 * without a catalogue, since the backend prices the order from it.
 */
export function useRegistryProducts(country: string | null, enabled: boolean) {
  const [products, setProducts] = useState<RegistryProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadedCountry, setLoadedCountry] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  /** Read the catalogue again after a failure. */
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!country || !enabled) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    vnextApiClient
      .listRegistryProducts(country, { signal: controller.signal })
      .then((result) => {
        if (!cancelled) {
          setProducts(result);
          setLoadedCountry(country);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const e = toError(err, 'Could not load the document catalogue');
        Sentry.captureException(e);
        setProducts([]);
        setLoadedCountry(null);
        setError(e);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [country, enabled, attempt]);

  return {
    // Never hand back another country's catalogue while this one loads.
    products: loadedCountry === country ? products : [],
    isLoading,
    error,
    reload,
  };
}

const EMPTY_RESULT: RegistryCompanySearchResult = { candidates: [], matchedResults: null, page: 1 };

/**
 * Find a company in Armadillo's index. Searching is free and explicit: the
 * caller runs `search` on the analyst's action, never on keystrokes. A
 * later search supersedes an earlier one still in flight.
 */
export function useRegistryCompanySearch() {
  const [result, setResult] = useState<RegistryCompanySearchResult>(EMPTY_RESULT);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => () => inFlight.current?.abort(), []);

  const search = useCallback(async (params: RegistryCompanySearchParams) => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setIsSearching(true);
    setError(null);
    try {
      const found = await vnextApiClient.searchRegistryCompanies(params, {
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setResult(found);
        setHasSearched(true);
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) {
        return;
      }
      const e = toError(err, 'Search failed');
      Sentry.captureException(e);
      setResult(EMPTY_RESULT);
      setHasSearched(true);
      setError(e);
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setIsSearching(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    setResult(EMPTY_RESULT);
    setError(null);
    setHasSearched(false);
    setIsSearching(false);
  }, []);

  return {
    candidates: result.candidates,
    matchedResults: result.matchedResults,
    isSearching,
    error,
    /** False until the first search resolves — "no results" means nothing before then. */
    hasSearched,
    search,
    reset,
  };
}

/** One key per set of countries, whatever their order or repeats: a batch
 * re-reads its catalogues when the set changes, not when an array does. */
export function countrySetKey(countries: string[]): string {
  return [...new Set(countries)].sort((a, b) => a.localeCompare(b)).join(',');
}

const NO_CATALOGUES: Record<string, RegistryProduct[]> = {};

interface CatalogueSet {
  key: string;
  /** The `attempt` the set was read under: a reload reads it again. */
  attempt: number;
  catalogues: Record<string, RegistryProduct[]>;
  /** The countries whose read failed, with the first failure's message. */
  failed: string[];
  error: Error | null;
}

async function readCatalogue(
  country: string,
  signal: AbortSignal
): Promise<readonly [string, RegistryProduct[] | Error]> {
  try {
    return [country, await vnextApiClient.listRegistryProducts(country, { signal })] as const;
  } catch (err: unknown) {
    return [country, toError(err, 'Could not load the document catalogue')] as const;
  }
}

function catalogueSetOf(
  key: string,
  attempt: number,
  results: (readonly [string, RegistryProduct[] | Error])[]
) {
  const set: CatalogueSet = { key, attempt, catalogues: {}, failed: [], error: null };
  for (const [country, result] of results) {
    if (result instanceof Error) {
      Sentry.captureException(result);
      set.failed.push(country);
      set.error ??= new Error(`${country}: ${result.message}`);
    } else {
      set.catalogues[country] = result;
    }
  }
  return set;
}

/**
 * The catalogues of several countries at once, for an order placed for
 * every company on a report. One request per distinct country, all in
 * flight together; a change of set drops the reads in flight and starts
 * over. `catalogues` is all or nothing: an intersection over some of the
 * countries would offer a document a missing country may not have.
 */
export function useRegistryProductsFor(countries: string[], enabled: boolean) {
  const [loaded, setLoaded] = useState<CatalogueSet | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const key = countrySetKey(countries);

  /** Read the catalogues again after a failure. */
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Never another set's catalogues while this one loads.
  const current = loaded?.key === key ? loaded : null;
  const complete = current !== null && current.failed.length === 0;
  // A complete set read under this attempt stays in hand: leaving the
  // documents step and coming back must not read every country again. A set
  // with a failed country is read again when re-enabled, as it is on reload.
  const inHand = complete && current.attempt === attempt;

  useEffect(() => {
    if (!key || !enabled || inHand) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setIsLoading(true);
    void Promise.all(
      key.split(',').map((country) => readCatalogue(country, controller.signal))
    ).then((results) => {
      if (!cancelled) {
        setLoaded(catalogueSetOf(key, attempt, results));
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, enabled, attempt, inHand]);

  return {
    catalogues: complete ? current.catalogues : NO_CATALOGUES,
    /** Every country's catalogue is in hand. */
    loaded: complete,
    isLoading,
    error: current?.error ?? null,
    failedCountries: current?.failed ?? [],
    reload,
  };
}
